import { Component, inject } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import {
  Validators,
  ValidatorFn,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { FormControl } from '@angular/forms';
import { AuthService } from '../../services/auth.service';

import { PasswordChangeRequest } from '../../interfaces/login.interface';
import { ModalEnvioCorrectoComponent } from '../../componentes/login/modal-envio-correcto/modal-envio-correcto.component';
import { PasswordToggleDirective } from '../../directives/password-toggle.directive';
import { GoogleAnalyticsService } from '../../services/google-analytics/google-analytics.service';

@Component({
  selector: 'app-cambiar-contrasena',
  standalone: true,
  imports: [
    CommonModule,
    ModalEnvioCorrectoComponent,
    ReactiveFormsModule,
    PasswordToggleDirective,
  ],
  templateUrl: './cambiar-contrasena.component.html',
  styleUrl: './cambiar-contrasena.component.css',
})
export class CambiarContrasenaComponent {
  loginForm: FormGroup;

  isSameActual: boolean = false;
  isSameNew: boolean = false;
  isSameConfirm: boolean = false;
  error: boolean = false;
  passwordRequirements = [
    { key: 'minLength', met: false, message: 'Mínimo 8 caracteres' },
    { key: 'upperCase', met: false, message: 'Al menos una letra mayúscula' },
    { key: 'lowerCase', met: false, message: 'Al menos una letra minúscula' },
    { key: 'number', met: false, message: 'Al menos un número' },
    {
      key: 'specialChar',
      met: false,
      message: 'Al menos un carácter especial',
    },
    {
      key: 'matchPassword',
      met: false,
      message: 'Las contraseñas deben coincidir',
    },
  ];

  allRequirementsMet: boolean = false;

  constructor(private authService: AuthService,private gAnalytics:GoogleAnalyticsService) {
    this.loginForm = new FormGroup({
      contrasenaActual: new FormControl('', [Validators.required]),
      contrasenaNueva: new FormControl('', [
        Validators.required,
        Validators.minLength(8),
        this.createPasswordValidator(),
      ]),
      confirmarContrasena: new FormControl('', [Validators.required]),
    });

    this.loginForm.get('contrasenaNueva')?.valueChanges.subscribe((value) => {
      this.validatePassword(value);
    });

    this.loginForm.get('confirmarContrasena')?.valueChanges.subscribe(() => {
      const newPassword = this.loginForm.get('contrasenaNueva')?.value;
      this.validatePassword(newPassword);
    });

    this.router = inject(Router);
  }


  onChangePassword() {
    if (this.loginForm.valid) {
      if (
        this.loginForm.get('contrasenaActual')?.value ===
        this.loginForm.get('contrasenaNueva')?.value
      ) {
        console.error('No puedes colocar la misma contraseña');
        this.isSameActual = true;
        return;
      }

      if (
        this.loginForm.get('contrasenaNueva')?.value !==
        this.loginForm.get('confirmarContrasena')?.value
      ) {
        console.error('Las contraseñas no coinciden');
        this.isSameConfirm = true;
        return;
      }
      const email = localStorage.getItem('email');
      const request: PasswordChangeRequest = {
        Email: email || '',
        OldPassword: this.loginForm.get('contrasenaActual')?.value,
        NewPassword: this.loginForm.get('contrasenaNueva')?.value,
      };

      this.authService.changePassword(request).subscribe({
        next: (response) => {
          if (response.data.successful) {

            this.gAnalytics.trackEvent('cambiar_contraseña',{
            page:'/cambiar-contrasena',
            action:'Cambio de contraseña exitoso'
          })

            this.showModal = true;
            this.loginForm.reset();
            this.error = false;
          }
        },
        error: (error) => {
          console.error('Error al cambiar contraseña:', error);

          // Verificar la estructura del error
          if (error.error?.data?.responseCode === 3) {
            this.isSameNew = true;
            this.error = false;
          } else if (error.status === 409) {
            this.isSameNew = true;
            this.error = false;
          } else {
            this.error = true;
          }

          // Agregar logs para depuración
          console.log('Estado de isSameNew:', this.isSameNew);
          console.log('Estructura del error:', {
            error: error,
            status: error.status,
            responseCode: error.error?.data?.responseCode,
          });
        },
      });
    } else {
      console.log('Formulario inválido');
    }
  }

  // Modal envio correcto
  showModal = false;
  router = inject(Router);

  onContinue(): void {
    this.showModal = false;
    this.router.navigate(['/iniciar-sesion']);
  }

  private createPasswordValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;

      if (!value) {
        return null;
      }

      const hasUpperCase = /[A-Z]/.test(value);
      const hasLowerCase = /[a-z]/.test(value);
      const hasNumber = /[0-9]/.test(value);
      const hasSpecialChar = /[^a-zA-Z0-9]/.test(value);
      const hasMinLength = value.length >= 8;

      const passwordValid =
        hasUpperCase &&
        hasLowerCase &&
        hasNumber &&
        hasSpecialChar &&
        hasMinLength;

      return !passwordValid ? { invalidPassword: true } : null;
    };
  }

  private validatePassword(password: string) {
    if (!password) {
      this.passwordRequirements.forEach((req) => {
        req.met = false;
      });
      return;
    }

    this.passwordRequirements.forEach((req) => {
      switch (req.key) {
        case 'minLength':
          req.met = password.length >= 8;
          break;
        case 'upperCase':
          req.met = /[A-Z]/.test(password);
          break;
        case 'lowerCase':
          req.met = /[a-z]/.test(password);
          break;
        case 'number':
          req.met = /[0-9]/.test(password);
          break;
        case 'specialChar':
          req.met = /[^a-zA-Z0-9]/.test(password);
          break;
        case 'matchPassword':
          const confirmPassword = this.loginForm.get(
            'confirmarContrasena'
          )?.value;
          req.met = password && confirmPassword && password === confirmPassword;
          break;
      }
    });

    this.allRequirementsMet = this.passwordRequirements.every((req) => req.met);
  }
}
